<script setup lang="ts">
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

defineProps<{
  services: SectionServicesListRecord['services']
  layout: SectionServicesListRecord['layout']
}>()

const { isDesktop } = useDevice()
const isImageLazy = ref(true)
const isPanelVisible = ref(false)
const activeIdx = ref<number | null>(null)
const ctxRef = ref<HTMLElement | null>(null)

let ctx: gsap.Context | null = null
const id = useId()

const setActive = (idx: number | null) => {
  activeIdx.value = idx
}

onMounted(() => {
  if (ctxRef.value) {
    gsap.registerPlugin(ScrollTrigger)

    const mm = gsap.matchMedia()
    mm.add('(min-width: 1024px)', () => {
      ctx = gsap.context((self: gsap.Context) => {
        ScrollTrigger.create({
          trigger: ctxRef.value,
          start: 'top bottom',
          once: true,
          onEnter: () => {
            if (!isImageLazy.value) {
              isImageLazy.value = true
            }
          }
        })

        const panels = self.selector ? self.selector('.fx-service-panel') : []

        if (panels.length) {
          panels.forEach((item: HTMLElement, idx: number) => {
            ScrollTrigger.create({
              trigger: item,
              start: 'top 56%',
              end: 'bottom 56%',
              onEnter: () => {
                setActive(idx)
                if (activeIdx.value === 0) {
                  isPanelVisible.value = true
                }
              },
              onLeave: () => {
                setActive(idx)
                if (activeIdx.value === panels.length - 1) {
                  isPanelVisible.value = false
                }
              },
              onLeaveBack: () => {
                setActive(idx)
                if (activeIdx.value === 0) {
                  isPanelVisible.value = false
                }
              },
              onEnterBack: () => {
                setActive(idx)
                if (activeIdx.value === panels.length - 1) {
                  isPanelVisible.value = true
                }
              }
            })
          })
        }
        ScrollTrigger.refresh()
      }, ctxRef.value as HTMLElement)
    })
  }
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <div
    ref="ctxRef"
    class="col-span-full flex h-fit flex-col gap-2 lg:col-span-12 lg:col-start-1 lg:gap-6 lg:py-40 xl:col-span-8 xl:col-start-5"
  >
    <template v-if="isDesktop">
      <Transition name="servicesMask">
        <div
          v-if="isPanelVisible"
          class="fixed right-0 top-1/2 hidden aspect-video h-auto w-full max-w-[55rem] translate-x-1/4 translate-y-[-41%] md:block 2xl:translate-x-0 hd:max-w-[64rem]"
        >
          <div v-for="(service, idx) in services" :id="id + '_idx-' + idx" :key="idx" :idx="idx">
            <Transition name="crossSwitch" mode="in-out">
              <Image
                v-show="activeIdx === idx"
                :loading="isImageLazy ? 'lazy' : 'eager'"
                class="fixed left-0 top-0 aspect-video w-full max-w-none object-cover"
                :src="
                  service.image?.responsiveImage
                    ? service.image?.responsiveImage?.src
                    : service.image?.url
                "
                :srcset="
                  service.image?.responsiveImage
                    ? service.image?.responsiveImage?.webpSrcSet
                    : undefined
                "
                :width="
                  service.image?.responsiveImage
                    ? service.image?.responsiveImage?.width
                    : (service.image?.width ?? 880)
                "
                :height="
                  service.image?.responsiveImage
                    ? service.image?.responsiveImage?.height
                    : (service.image?.height ?? 495)
                "
                :alt="service.image?.alt ?? ''"
                sizes="auto"
              />
            </Transition>
          </div>
        </div>
      </Transition>
    </template>
    <ServicesPanel
      v-for="(service, idx) in services"
      :id="id + '_idx-' + idx"
      :key="idx"
      :idx="idx"
      :active="activeIdx === idx"
      :layout="layout"
      class="fx-service-panel relative lg:pointer-events-none"
      @expand="activeIdx = activeIdx !== idx ? idx : null"
    >
      <template #title
        ><span v-if="service.title" class="block">
          {{ service.title }}
        </span></template
      >
      <template #content>
        <div
          class="absolute right-0 top-0 w-full max-w-[16.25rem] translate-x-4 lg:hidden lg:max-w-4xl"
        >
          <Transition name="maskedScale">
            <Image
              v-if="service.image && activeIdx === idx"
              loading="lazy"
              class="aspect-16/10 w-full max-w-none object-cover"
              :src="
                service.image.responsiveImage
                  ? service.image.responsiveImage?.src
                  : service.image.url
              "
              :srcset="
                service.image.responsiveImage
                  ? service.image.responsiveImage?.webpSrcSet
                  : undefined
              "
              :width="
                service.image.responsiveImage
                  ? service.image.responsiveImage?.width
                  : (service.image.width ?? 880)
              "
              :height="
                service.image.responsiveImage
                  ? service.image.responsiveImage?.height
                  : (service.image.height ?? 550)
              "
              :alt="service.image?.alt ?? ''"
              sizes="auto"
            />
          </Transition>
        </div>
      </template>
    </ServicesPanel>
  </div>
</template>
